/**
 * Compose pipeline.
 *
 * Steps, exactly as Section 6 specifies:
 *   1. POST /compose/prepare → { messages, valid_until, pending_id }
 *   2. Add an optimistic bubble to `pendingStore` (status='signing')
 *   3. Call `tonConnectUI.sendTransaction({messages, validUntil})`
 *   4a. On reject → remove the bubble, fire DELETE /compose/pending/:id
 *   4b. On success → flip the bubble to status='pending'
 *   5. SSE pending_resolved (handled in useStream) will flip to 'confirmed'
 *      and the real ThreadMessage arriving via SSE will drop the bubble.
 */
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTonConnectUI } from "@tonconnect/ui-react";
import { composeCancel, composePrepare } from "../lib/api";
import { pendingStore } from "../lib/pending";
import { haptics } from "../lib/telegram";
import { bumpInboxRow } from "./useStream";

export type SendStatus = "idle" | "preparing" | "signing" | "sent" | "rejected" | "error";

export interface SendArgs {
  to: string;
  /** Canonical 0:hex form, used for the optimistic pending row + inbox bump. */
  toRaw: string;
  comment: string;
  amount_nano: string;
}

export function useCompose(): {
  status: SendStatus;
  errorMessage: string | null;
  send: (args: SendArgs) => Promise<void>;
  reset: () => void;
} {
  const [tonConnectUI] = useTonConnectUI();
  const qc = useQueryClient();
  const [status, setStatus] = useState<SendStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const send = useCallback(
    async (args: SendArgs) => {
      setStatus("preparing");
      setErrorMessage(null);
      let pendingId: string | null = null;
      try {
        const prep = await composePrepare({
          to: args.to,
          comment: args.comment,
          amount_nano: args.amount_nano,
        });
        pendingId = prep.pending_id;

        // Optimistic bubble.
        pendingStore.add({
          pending_id: prep.pending_id,
          counterparty_raw: args.toRaw,
          comment: args.comment,
          amount_nano: args.amount_nano,
        });

        // Inbox bump — gives the user the "I just sent" feedback even if
        // they back out to the inbox immediately.
        bumpInboxRow(qc, args.toRaw, {
          comment: args.comment,
          amount_nano: args.amount_nano,
        });

        setStatus("signing");
        haptics.impact("medium");

        await tonConnectUI.sendTransaction({
          validUntil: prep.valid_until,
          messages: prep.messages.map((m) => ({
            address: m.address,
            amount: m.amount,
            payload: m.payload,
          })),
        });

        // User signed. Flip the bubble; SSE will resolve to confirmed later.
        pendingStore.setStatus(prep.pending_id, "pending");
        setStatus("sent");
        haptics.notify("success");
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        // TonConnectUI throws a `UserRejectsError` (or similar) on cancel —
        // best heuristic is the message string since the SDK doesn't export
        // a discriminator.
        const rejected =
          /reject/i.test(msg) ||
          /cancel/i.test(msg) ||
          /UserRejects/.test(msg);

        if (pendingId !== null) {
          if (rejected) {
            pendingStore.remove(pendingId);
            void composeCancel(pendingId);
          } else {
            // Wallet error mid-flight — keep the bubble in `failed` so the
            // user can see what happened and retry.
            pendingStore.setStatus(pendingId, "failed");
          }
        }
        haptics.notify("error");
        setStatus(rejected ? "rejected" : "error");
        if (!rejected) setErrorMessage(msg);
      }
    },
    [qc, tonConnectUI],
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setErrorMessage(null);
  }, []);

  return { status, errorMessage, send, reset };
}
